import { useState, useEffect } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const banners = [
  { id: 1, title: 'Discover Amazing Architecture', description: 'Explore inspiring projects from talented architects worldwide' },
  { id: 2, title: 'Showcase Your Work', description: 'Share your architectural projects with a global community' },
  { id: 3, title: 'Connect & Collaborate', description: 'Network with architects and designers worldwide' },
];

export function BannerCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => setCurrentIndex((prev) => (prev + 1) % banners.length), 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const pause = () => {
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToSlide = (index: number) => { setCurrentIndex(index); pause(); };
  const goToPrevious = () => { setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length); pause(); };
  const goToNext = () => { setCurrentIndex((prev) => (prev + 1) % banners.length); pause(); };

  const currentBanner = banners[currentIndex];

  return (
    <div className="banner-hero relative rounded-3xl overflow-hidden group h-[120px] md:h-[180px] lg:h-[200px] border border-border">
      {/* Overlay — light mode: white wash to keep muted feel; dark mode: dark wash */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/60" />

      {/* Mockup image — flipped, pinned to right, slightly oversized */}
      <img
        src="/mockup.png"
        alt=""
        aria-hidden="true"
        className="absolute right-0 bottom-0 h-[120%] object-contain object-bottom scale-x-[-1] pointer-events-none select-none"
      />

      <div className="relative z-10 p-4 md:p-6 lg:p-8 h-full flex flex-col justify-center">
        <div className="max-w-2xl">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-1 md:mb-2 uppercase tracking-wide">{currentBanner.title}</h1>
          <p className="text-xs md:text-sm text-muted-foreground">{currentBanner.description}</p>
        </div>

        {/* Prev / Next */}
        <button
          onClick={goToPrevious}
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background/60 text-foreground hover:bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Previous"
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </button>
        <button
          onClick={goToNext}
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background/60 text-foreground hover:bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Next"
        >
          <ChevronRightIcon className="h-6 w-6" />
        </button>

        {/* Dots — desktop only */}
        <div className="hidden md:flex gap-2 justify-center mt-3 md:mt-4">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all ${index === currentIndex ? 'w-6 bg-foreground' : 'w-2 bg-foreground/30 hover:bg-foreground/50'}`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
